import { Grid } from "@mui/material";
import { table10Rows } from "features/analytics";
import StandardAnalyticsChartStatCard from "features/analytics/components/StandardAnalyticsChartStatCard";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { AnalyticsSpacing } from "consts";
import { useMemo } from "react";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_ECOM_INSIGHTS_DAYS_TO_CONVERSION,
  CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_BY_CHANNEL_PIE,
  CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_BY_PRODUCT,
  CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_STAT_CUSTOMER_CONVERSION_RATE,
  CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_STAT_NEW_CUSTOMER_CAC,
  CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_STAT_TOTAL_NEW_CUSTOMERS,
  CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMER_REVENUE,
  CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_TREND,
  CHART_KEY_ECOM_INSIGHTS_VISITS_TO_CONVERSION,
} from "features/analytics/constants/chartKeys";

function NewCustomerInsights() {
  const newCustomerAttribute = useMemo(() => {
    return {
      key: ApiDimension.DIMENSION_CUSTOMER_TYPE,
      operator: ApiOperator.EQUAL,
      value: "New",
    };
  }, []);

  return (
    <Grid container spacing={AnalyticsSpacing} mb={4}>
      <Grid item xs={4}>
        <StandardAnalyticsChartStatCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_STAT_TOTAL_NEW_CUSTOMERS}
          chart={{
            name: "Total New Customers",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_NEW_CUSTOMERS_COUNT],
              },
            ],
            displayOptions: {
              statTitle: "Total New Customers",
            },
            comparison: true,
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <StandardAnalyticsChartStatCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_STAT_NEW_CUSTOMER_CAC}
          chart={{
            name: "New Customer CAC",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_CAC],
              },
            ],
            displayOptions: {
              statTitle: "New Customer CAC",
            },
            comparison: true,
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <StandardAnalyticsChartStatCard
          chartKey={
            CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_STAT_CUSTOMER_CONVERSION_RATE
          }
          chart={{
            name: "Customer Conversion Rate",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_TO_NEW_CUSTOMER_RATE],
              },
            ],
            displayOptions: {
              statTitle: "Customer Conversion Rate",
            },
            comparison: true,
          }}
        />
      </Grid>
      <Grid item xs={8} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMER_REVENUE}
          chart={{
            name: "New Customer Revenue",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [newCustomerAttribute],
            displayOptions: {
              noFilters: true,
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_REVENUE],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_TREND}
          chart={{
            name: "Total New Customers",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_WEEK],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            displayOptions: {
              noFilters: true,
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_NEW_CUSTOMERS_COUNT],
                chart: {
                  [ApiMetric.METRIC_NEW_CUSTOMERS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_VISITS_TO_CONVERSION}
          chart={{
            name: "Number of Visits to Conversion",
            dimension: [
              ApiDimension.DIMENSION_VISITS_TO_FIRST_MACRO_CONVERSION,
            ],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            orderBy: `${ApiDimension.DIMENSION_VISITS_TO_FIRST_MACRO_CONVERSION}`,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_LEADS_COUNT],
                chart: {
                  [ApiMetric.METRIC_LEADS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_DAYS_TO_CONVERSION}
          chart={{
            name: "Number of Days to Conversion",
            dimension: [ApiDimension.DIMENSION_DAYS_TO_FIRST_MACRO_CONVERSION],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            orderBy: `${ApiDimension.DIMENSION_DAYS_TO_FIRST_MACRO_CONVERSION}`,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_LEADS_COUNT],
                chart: {
                  [ApiMetric.METRIC_LEADS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
            ],
          }}
        />
      </Grid>

      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_BY_CHANNEL_PIE}
          chart={{
            name: "New Customers by Channel",
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: [],
            includeEmpty: true,
            displayOptions: {
              showTotals: true,
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_NEW_CUSTOMERS_COUNT],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ minHeight: table10Rows }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_NEW_CUSTOMERS_BY_PRODUCT}
          chart={{
            name: "New customers and new customer revenue by product",
            dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.TABLE,
            attributes: [newCustomerAttribute],
            displayOptions: {
              noFilters: true,
              noTitle: true,
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_NEW_CUSTOMERS_COUNT,
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_COUNT,
                  ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
                  ApiMetric.METRIC_PRODUCT_REVENUE,
                  ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                  ApiMetric.METRIC_PRODUCT_IMPRESSION_RATE,
                  ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_COUNT,
                  ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
                  ApiMetric.METRIC_SESSION_SALES_COUNT, // needs checking
                  ApiMetric.METRIC_SALES_COMPLETION_RATE,
                  ApiMetric.METRIC_SALES_REVENUE,
                  ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                ],
              },
            ],
          }}
        />
      </Grid>
    </Grid>
  );
}

export default NewCustomerInsights;
